import { startCase, upperFirst } from 'lodash'
import React from 'react'
import { BrowserRouter, Route } from 'react-router-dom'

import {
  ErrorContextWrapper,
  SuperModalProvider,
  ToastCustomContainer,
  withMaintenanceMode,
  ModalProvider,
} from 'packages/common'
import { IconName } from 'packages/iconic'
import { SegmentTracker } from 'packages/wiretap/src/tracking/SegmentTracker'

import {
  reducer as timersReducer,
  initialState as initialTimersState,
} from 'app/fieldapp/components/timers/state/timers.reducer'
import { Slugs, useI18n } from 'app/fieldapp/i18n'

import { ConnectedAdminPanel } from './admin'
import {
  NavTabWrapper,
  NavTabConfig,
  OfflineTimersBannerContainer,
} from './core'
import { ConnectedFieldAppInitializer } from './core/components/FieldAppInitializer'
import { FieldAppRouter } from './FieldApp.router'
import { ConnectedTimers } from './timers'
import { TimersContext } from './timers/state/timers.context'

export type FieldAppRootProps = {
  initialUrl: string
  isAdmin: boolean
}

export const FieldAppRoot: React.FC<FieldAppRootProps> = ({
  isAdmin,
  initialUrl,
}) => {
  const { t } = useI18n()

  const cleansTab = {
    icon: IconName.calendar,
    route: '/cleans',
    text: startCase(t(Slugs.tasks)),
  }

  const profileTab = {
    icon: IconName.user,
    route: '/profile',
    text: upperFirst(t(Slugs.profile)),
  }

  const ticketsTab = {
    icon: IconName.tag,
    route: '/tickets',
    text: upperFirst(t(Slugs.tickets)),
  }

  const navTabConfig: NavTabConfig[] = [cleansTab, ticketsTab, profileTab]

  const [timersState, timersDispatch] = React.useReducer(
    timersReducer,
    initialTimersState,
  )
  const timersContextValue = React.useMemo(
    () => ({ dispatch: timersDispatch, state: timersState }),
    [timersState],
  )

  return (
    <ErrorContextWrapper contextName={'fieldapp-error-context'}>
      <ToastCustomContainer />
      <SuperModalProvider>
        <ModalProvider>
          <TimersContext.Provider value={timersContextValue}>
            <ConnectedFieldAppInitializer />
            <ConnectedTimers />
            <BrowserRouter>
              <OfflineTimersBannerContainer />
              <Route
                render={({ history, location }) => (
                  <NavTabWrapper
                    configs={navTabConfig}
                    current={location.pathname}
                    navigateTo={history.push}
                  />
                )}
              />
              <SegmentTracker />

              <main>
                {isAdmin && <ConnectedAdminPanel />}
                <FieldAppRouter initialUrl={initialUrl} />
              </main>
            </BrowserRouter>
          </TimersContext.Provider>
        </ModalProvider>
      </SuperModalProvider>
    </ErrorContextWrapper>
  )
}

export default withMaintenanceMode(FieldAppRoot)
